import React, { useState, useEffect } from "react";

export default function OnboardingStep({
  step,
  value,
  onComplete,
  onPrevious,
  isLastStep,
  submitting,
}) {
  const emptyForType = step.type === "multi-select" ? [] : "";
  const [localValue, setLocalValue] = useState(value ?? emptyForType);

  // Sync local state when step or value changes so each step shows only its own value (e.g. company_name stays empty, not previous step's selection)
  useEffect(() => {
    const empty = step.type === "multi-select" ? [] : "";
    setLocalValue(value ?? empty);
  }, [step.id, step.type, value]);

  const handleChange = (newValue) => {
    setLocalValue(newValue);
  };

  const handleNext = () => {
    if (step.required && !localValue) {
      return; // Don't proceed if required field is empty
    }

    // Validate multi-select has at least one option
    if (step.type === "multi-select" && step.required && localValue.length === 0) {
      return;
    }

    onComplete(step.id, localValue);
  };

  const optionBase =
    "w-full text-left rounded-lg bg-white min-h-[48px] px-4 py-3 font-Mulish touch-manipulation";
  const optionSelected =
    "border-2 border-primary-500 text-secondary";
  const optionUnselected =
    "border border-hairline text-secondary hover:border-primary-200";
  const optionDisabled = submitting ? "pointer-events-none opacity-60" : "";

  const renderInput = () => {
    switch (step.type) {
      case "select":
        return (
          <div className={`space-y-3 sm:space-y-4 ${optionDisabled}`}>
            {step.options.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={submitting}
                  onClick={() => handleChange(option.value)}
                  className={`${optionBase} flex items-start gap-3 ${
                    localValue === option.value ? optionSelected : optionUnselected
                  }`}
                >
                  {Icon && (
                    <Icon
                      className={`h-5 w-5 shrink-0 mt-0.5 ${localValue === option.value ? "text-primary-700" : "text-graphite"}`}
                      aria-hidden
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <span className={localValue === option.value ? "font-semibold" : ""}>{option.label}</span>
                    {option.description && (
                      <span className="mt-0.5 block text-sm text-graphite">
                        {option.description}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );

      case "multi-select":
        return (
          <div className={`space-y-3 sm:space-y-4 ${optionDisabled}`}>
            {step.options.map((option) => {
              const isSelected = localValue.includes(option.value);
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={submitting}
                  onClick={() => {
                    const newValue = isSelected
                      ? localValue.filter((v) => v !== option.value)
                      : [...localValue, option.value];
                    handleChange(newValue);
                  }}
                  className={`${optionBase} flex items-center justify-between gap-3 ${
                    isSelected ? optionSelected : optionUnselected
                  }`}
                >
                  <div className="min-w-0 flex-1 flex items-start gap-3">
                    {Icon && (
                      <Icon
                        className={`h-5 w-5 shrink-0 mt-0.5 ${isSelected ? "text-primary-700" : "text-graphite"}`}
                        aria-hidden
                      />
                    )}
                    <div className="min-w-0">
                      <span className={isSelected ? "font-semibold" : ""}>{option.label}</span>
                      {option.description && (
                        <span className="mt-0.5 block text-sm text-graphite">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <svg
                      className="h-5 w-5 shrink-0 text-primary-700"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        );

      case "text":
        return (
          <input
            type="text"
            value={localValue}
            disabled={submitting}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={step.placeholder || ""}
            className="min-h-[48px] w-full rounded-lg border-0 bg-white px-3 py-2 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-60"
          />
        );

      default:
        return null;
    }
  };

  const canProceed = step.required
    ? step.type === "multi-select"
      ? localValue.length > 0
      : localValue !== ""
    : true;

  return (
    <div>
      <h2 className="mb-2 font-Mulish text-lg font-semibold text-secondary">
        {step.question}
      </h2>
      {step.subtitle && (
        <p className="mb-1 font-Mulish text-sm text-graphite">{step.subtitle}</p>
      )}
      {step.required && !step.subtitle && (
        <p className="mb-1 font-Mulish text-sm text-graphite">Required</p>
      )}
      <div className="mt-5 mb-8">{renderInput()}</div>

      {/* Action Buttons — stack on small screens for touch targets */}
      <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        {onPrevious && (
          <button
            type="button"
            disabled={submitting}
            onClick={onPrevious}
            className="min-h-[48px] rounded-full border border-primary-200 bg-white px-6 py-3 font-Mulish text-sm text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Previous
          </button>
        )}
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed || submitting}
          className={`min-h-[48px] flex-1 rounded-full px-8 py-3 font-Mulish text-sm font-semibold sm:flex-initial ${
            canProceed && !submitting
              ? "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
              : "cursor-not-allowed bg-primary-100 text-primary-800"
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2" aria-live="polite">
              <svg
                className="h-5 w-5 shrink-0 animate-spin text-primary-800"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Saving…
            </span>
          ) : isLastStep ? (
            "Complete"
          ) : (
            "Next"
          )}
        </button>
      </div>
    </div>
  );
}

